import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';

const STORAGE_KEY = 'color-mode'; // 'system' | 'light' | 'dark'
const PREFERENCES = ['system', 'light', 'dark'];

const ColorModeContext = createContext({
  mode: 'light', // resolved 'light' | 'dark'
  preference: 'system', // what the user picked
  isDark: false,
  setPreference: () => {},
  cycleMode: () => {},
});

function getStoredPreference() {
  if (typeof window === 'undefined') return 'system';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return PREFERENCES.includes(saved) ? saved : 'system';
}

function getSystemMode() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Owns the color-mode preference and resolves it against the live OS setting.
 * Preference 'system' follows the OS in real time; 'light'/'dark' pin it.
 * Wrap the app once; read it anywhere with useColorMode.
 */
export function ColorModeProvider({ children }) {
  const [preference, setPreferenceState] = useState(getStoredPreference);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  // Track the OS setting live, regardless of the user's choice, so that
  // switching back to 'system' (or starting there) updates immediately.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setPreference = useCallback((pref) => {
    window.localStorage.setItem(STORAGE_KEY, pref);
    setPreferenceState(pref);
  }, []);

  const cycleMode = useCallback(() => {
    setPreferenceState((prev) => {
      const next = PREFERENCES[(PREFERENCES.indexOf(prev) + 1) % PREFERENCES.length];
      window.localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const mode = preference === 'system' ? systemMode : preference;

  // Keep native UI (scrollbars, form controls) in sync with the theme.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(
    () => ({ mode, preference, isDark: mode === 'dark', setPreference, cycleMode }),
    [mode, preference, setPreference, cycleMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  return useContext(ColorModeContext);
}
